import { format } from 'date-fns';
import { Copy, FileText, Store, User2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/common/components/Badge';
import { DisplayInfoCard } from '@/common/components/DisplayInfoCard';
import { useLabelFor } from '@/common/hooks/useLabelFor';
import { useToast } from '@/common/hooks/useToast';
import { useTranslateFormPayment } from '@/common/hooks/useTranslateFormPayment';
import { useTranslateGenre } from '@/common/hooks/useTranslateGenre';
import { useTranslateSalesModality } from '@/common/hooks/useTranslateSalesModality';
import { maskCompanyDocument, maskTelephone } from '@/common/utils';
import { capitalizeString } from '@/common/utils/capitalizeString';
import { useSelectedDebitNote } from '@/modules/invoicing/hooks/useSelectedDebitNote';

import * as style from './styles';

export function GeneralData() {
  const { t } = useTranslation(['invoicing/openInvoices', 'invoices/invoices']);
  const { selectedDebitNote } = useSelectedDebitNote();
  const translatorSalesModality = useTranslateSalesModality();
  const translateGenre = useTranslateGenre();
  const translateFormPayment = useTranslateFormPayment();
  const { companyLabel } = useLabelFor();
  const { toast } = useToast();

  if (!selectedDebitNote) {
    return null;
  }

  const sections = [
    {
      icon: Store,
      title: selectedDebitNote?.Customer.name || '',
      cols: 1 as const,
      info: [
        `${t('clientSAPCode')} ${selectedDebitNote?.Customer.externalCode}`,
        `${companyLabel}: ${maskCompanyDocument(
          selectedDebitNote?.Customer.documentNumber || '',
        )}`,
      ],
    },
    {
      icon: User2,
      title: selectedDebitNote?.Sign.SignPerson[0].name || '',
      cols: 2 as const,
      info: [
        selectedDebitNote?.Sign.SignPerson[0].email || '',
        `${t('common:genre')} ${
          translateGenre[
            selectedDebitNote?.Sign?.SignPerson[0].genre || 'OTHER'
          ]
        }`,
        selectedDebitNote?.Sign.SignPerson[0].birthDate
          ? `${t('birthDate')} ${format(
              new Date(selectedDebitNote?.Sign.SignPerson[0].birthDate),
              'dd/MM/yyyy',
            )}`
          : '',
        maskTelephone(selectedDebitNote?.Sign?.SignPerson[0].phone || ''),
      ],
    },
    {
      icon: FileText,
      title: selectedDebitNote?.numberDebitNote || '',
      cols: 3 as const,
      info: [
        selectedDebitNote?.DebitNoteSignChassisInstallments[0]
          ?.SignChassisInstallments.SignChassis?.startDate
          ? `${t('startDate')} ${format(
              new Date(
                selectedDebitNote.DebitNoteSignChassisInstallments[0].SignChassisInstallments.SignChassis.startDate,
              ),
              'dd/MM/yyyy',
            )}`
          : '',
        selectedDebitNote?.isSingleInvoice
          ? `${t('installment')}: 1/1`
          : `${t('installment')}: ${
              selectedDebitNote?.DebitNoteSignChassisInstallments[0]
                ?.SignChassisInstallments.numberInstallment
            }/${
              selectedDebitNote?.DebitNoteSignChassisInstallments[0]
                ?.SignChassisInstallments.SignChassis.quantityInstallments
            }`,
        selectedDebitNote?.Sign.formPayment
          ? `${t('modality')} ${capitalizeString(
              translateFormPayment[selectedDebitNote.Sign.formPayment],
            )}`
          : '',
        selectedDebitNote?.DebitNoteSignChassisInstallments[0]
          ?.SignChassisInstallments.SignChassis?.endDate
          ? `${t('endDate')} ${format(
              new Date(
                selectedDebitNote.DebitNoteSignChassisInstallments[0].SignChassisInstallments.SignChassis.endDate,
              ),
              'dd/MM/yyyy',
            )}`
          : '',
        `${t('plan')} ${selectedDebitNote?.Sign.Deal.Plan.title}`,
        translatorSalesModality[selectedDebitNote.salesModality],
      ],
    },
  ];

  return (
    <div className={style.container()}>
      <h3 className={style.title()}>
        {t('contractData')}
        {selectedDebitNote?.Sign.Deal.dealCode && (
          <>
            <div className="whitespace-nowrap">
              - {selectedDebitNote.Sign.Deal.dealCode}
            </div>
            <div className="flex flex-row w-full justify-between">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(
                    selectedDebitNote?.Sign.Deal.dealCode,
                  );
                  toast.success(t('invoices/invoices:dealCodeToast'));
                }}
              >
                <Copy size={18} />
              </button>
              {selectedDebitNote?.isSingleInvoice && (
                <div className="hidden sm:flex">
                  <Badge
                    title={t('invoices/invoices:singleInvoice').toUpperCase()}
                    type="warning"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </h3>
      <DisplayInfoCard key={selectedDebitNote?.id} sections={sections} />
    </div>
  );
}
